import axios from 'axios';
import toast from 'react-hot-toast';
import deploymentConfig from '../config/deployment';

// Dynamically determine API URL based on deployment config or hostname
const getApiBaseUrl = () => {
  // First check deployment config
  if (deploymentConfig.apiBaseUrl) {
    // deploymentConfig.apiBaseUrl should already include /api if needed
    return deploymentConfig.apiBaseUrl;
  }

  // Check environment variable (should already include /api suffix)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Fallback to hostname-based detection
  const hostname = window.location.hostname;
  if (hostname === 'pixi-latte.local') {
    return 'http://pixi-latte.local:3001/api';
  }

  // Final fallback for local development
  return 'http://localhost:3001/api';
};

// Create axios instance with default config
const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: deploymentConfig.apiTimeout || 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token and language
api.interceptors.request.use(
  (config) => {
    // Use CMS tokens for admin interface
    const token = localStorage.getItem('cms_accessToken') || localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add content language header for localized content
    const contentLanguage = localStorage.getItem('cms_language') || 'en';
    config.headers['Accept-Language'] = contentLanguage;

    // Also add as query param for APIs that prefer it
    if (!config.params) {
      config.params = {};
    }
    if (!config.params.locale) {
      config.params.locale = contentLanguage;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    console.log('[API INTERCEPTOR] Response error:', error.response?.status, error.message);
    console.log('[API INTERCEPTOR] Request URL:', originalRequest?.url);
    console.log('[API INTERCEPTOR] Full URL:', originalRequest?.baseURL + originalRequest?.url);

    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('[API INTERCEPTOR] 401 detected on:', originalRequest?.url);
      console.log('[API INTERCEPTOR] Attempting token refresh...');
      originalRequest._retry = true;

      // Try CMS refresh token first, then regular refresh token
      const refreshToken = localStorage.getItem('cms_refreshToken') || localStorage.getItem('refreshToken');
      console.log('[API INTERCEPTOR] Refresh token available:', refreshToken ? 'Yes' : 'No');
      if (refreshToken) {
        try {
          const refreshEndpoint = localStorage.getItem('cms_refreshToken')
            ? '/cms/auth/refresh'
            : '/auth/refresh';
          console.log('[API INTERCEPTOR] Calling refresh endpoint:', api.defaults.baseURL + refreshEndpoint);

          const response = await api.post(refreshEndpoint, { refreshToken });

          const { tokens } = response.data;
          const { accessToken, refreshToken: newRefreshToken } = tokens;
          
          if (localStorage.getItem('cms_refreshToken')) {
            localStorage.setItem('cms_accessToken', accessToken);
            localStorage.setItem('cms_refreshToken', newRefreshToken);
          } else {
            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', newRefreshToken);
          }

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);

        } catch (refreshError) {
          console.error('[API INTERCEPTOR] Token refresh failed:', refreshError.message);
          console.error('[API INTERCEPTOR] Clearing all tokens and redirecting to login');
          // Refresh failed, redirect to login
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('cms_accessToken');
          localStorage.removeItem('cms_refreshToken');
          localStorage.removeItem('cms_user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        console.log('[API INTERCEPTOR] No refresh token found, redirecting to login');
        // No refresh token, redirect to login
        window.location.href = '/login';
      }
    } else if (error.response?.status === 404) {
      console.log('[API INTERCEPTOR] 404 error - NOT clearing tokens, just passing through');
    }

    // Handle other error responses
    if (error.response) {
      const errorMessage = error.response.data?.message || 'An error occurred';
      
      // Don't show toast for certain status codes
      if (![401, 403].includes(error.response.status)) {
        toast.error(errorMessage);
      }
    } else if (error.request) {
      toast.error('Network error. Please check your connection.');
    } else {
      toast.error('An unexpected error occurred.');
    }

    return Promise.reject(error);
  }
);

// API service functions
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  logout: (refreshToken) => api.post('/auth/logout', { refreshToken }),
  getProfile: () => api.get('/auth/profile'),
  refreshToken: (refreshToken) => api.post('/auth/refresh', { refreshToken })
};

export const usersAPI = {
  getUsers: (params) => api.get('/users', { params }),
  getUserById: (userId) => api.get(`/users/${userId}`),
  updateUser: (userId, userData) => api.patch(`/users/${userId}`, userData),
  adminUpdateUser: (userId, userData) => api.patch(`/users/admin/${userId}`, userData),
  deleteUser: (userId) => api.delete(`/users/${userId}`),
  banUser: (userId, banData) => api.post(`/users/${userId}/ban`, banData),
  unbanUser: (userId) => api.post(`/users/${userId}/unban`),
  updateAdminProfile: (userData) => api.patch('/users/profile/admin', userData),
  getUserActivity: (userId, params) => api.get(`/users/${userId}/activity`, { params }),
  getLeaderboard: (params) => api.get('/users/leaderboard/global', { params }),
  uploadAvatar: (avatarFile) => {
    const formData = new FormData();
    formData.append('avatar', avatarFile);
    return api.post('/users/avatar/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  removeAvatar: () => api.delete('/users/avatar')
};

export const achievementsAPI = {
  getUserAchievements: (userId, params) => api.get(`/achievements/user/${userId}`, { params }),
  createAchievement: (achievementData) => api.post('/achievements', achievementData),
  getAchievementStats: (userId) => api.get(`/achievements/stats/${userId}`),
  getLeaderboard: (params) => api.get('/achievements/leaderboard', { params }),
  getRecent: (params) => api.get('/achievements/recent', { params })
};

export const challengesAPI = {
  getChallenges: (params) => api.get('/challenges', { params }),
  getChallengeById: (challengeId) => api.get(`/challenges/${challengeId}`),
  createChallenge: (challengeData) => api.post('/challenges', challengeData),
  updateChallenge: (challengeId, challengeData) => api.patch(`/challenges/${challengeId}`, challengeData),
  deleteChallenge: (challengeId) => api.delete(`/challenges/${challengeId}`),
  restoreChallenge: (challengeId) => api.post(`/challenges/${challengeId}/restore`),
  cloneChallenge: (challengeId) => api.post(`/challenges/${challengeId}/clone`),
  permanentDeleteChallenge: (challengeId) => api.delete(`/challenges/${challengeId}/permanent`),
  bulkUpdateActive: (challengeIds, isActive) => api.patch('/challenges/bulk/active', { challengeIds, isActive }),
  getNearbyChallenges: (latitude, longitude, params) => 
    api.get(`/challenges/nearby/${latitude}/${longitude}`, { params }),
  uploadIcon: (challengeId, iconFile) => {
    const formData = new FormData();
    formData.append('icon', iconFile);
    return api.post(`/challenges/${challengeId}/icon`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  deleteIcon: (challengeId) => api.delete(`/challenges/${challengeId}/icon`),
  rateChallenge: (challengeId, data) => api.post(`/challenges/${challengeId}/rate`, data),
  getChallengeRatings: (challengeId, params) => api.get(`/challenges/${challengeId}/ratings`, { params }),
  getMyRating: (challengeId) => api.get(`/challenges/${challengeId}/my-rating`)
};

export const routeChallengesAPI = {
  setWaypoints: (challengeId, waypoints) =>
    api.post(`/route-challenges/${challengeId}/waypoints`, { waypoints }),
  getWaypoints: (challengeId) =>
    api.get(`/route-challenges/${challengeId}/waypoints`),
  deleteWaypoint: (challengeId, waypointId) =>
    api.delete(`/route-challenges/${challengeId}/waypoints/${waypointId}`),
};

export const locationsAPI = {
  trackLocation: (locationData) => api.post('/locations/track', locationData),
  getUserLocations: (userId, params) => api.get(`/locations/user/${userId}/recent`, { params }),
  detectAchievements: (locationData) => api.post('/locations/detect-achievements', locationData)
};

export const nftsAPI = {
  mintNFT: (achievementId) => api.post(`/nfts/achievements/${achievementId}/mint`),
  getUserNFTs: (userId) => api.get(`/nfts/user/${userId}`),
  getUserBlockchainNFTs: (userId) => api.get(`/users/${userId}/blockchain-nfts`),
  getUserNFTsFromDB: (userId) => api.get(`/users/${userId}/nfts`),
  getNFTTemplates: () => api.get('/nfts/templates'),
  connectWallet: (walletAddress) => api.post('/nfts/connect-wallet', { walletAddress }),
  disconnectWallet: () => api.post('/nfts/disconnect-wallet'),
  getNFTStats: () => api.get('/nfts/stats')
};

export const eventsAPI = {
  // CMS endpoints
  getEvents: (params) => api.get('/cms/events', { params }),
  getEventStats: () => api.get('/cms/events/stats'),
  getEventById: (id) => api.get(`/cms/events/${id}`),
  createEvent: (data) => api.post('/cms/events', data),
  updateEvent: (id, data) => api.put(`/cms/events/${id}`, data),
  deleteEvent: (id) => api.delete(`/cms/events/${id}`),
  restoreEvent: (id) => api.post(`/cms/events/${id}/restore`),
  uploadHeaderImage: (id, file) => {
    const formData = new FormData();
    formData.append('header', file);
    return api.post(`/cms/events/${id}/header-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Challenge & NFT linking
  createChallenge: (id) => api.post(`/cms/events/${id}/create-challenge`),
  linkChallenge: (id, challengeId) => api.post(`/cms/events/${id}/link-challenge`, { challenge_id: challengeId }),
  unlinkChallenge: (id) => api.delete(`/cms/events/${id}/unlink-challenge`),
  createNFT: (id, data) => api.post(`/cms/events/${id}/create-nft`, data),
  // Registrations
  getRegistrations: (id) => api.get(`/cms/events/${id}/registrations`),
  // Calendar
  importIcal: (file, options) => {
    const formData = new FormData();
    formData.append('ical', file);
    if (options?.auto_publish) formData.append('auto_publish', options.auto_publish);
    if (options?.default_category) formData.append('default_category', options.default_category);
    return api.post('/cms/events/import-ical', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Calendar sources
  getCalendarSources: () => api.get('/cms/events/calendar-sources'),
  addCalendarSource: (data) => api.post('/cms/events/calendar-sources', data),
  updateCalendarSource: (id, data) => api.put(`/cms/events/calendar-sources/${id}`, data),
  deleteCalendarSource: (id) => api.delete(`/cms/events/calendar-sources/${id}`),
  syncCalendarSource: (id) => api.post(`/cms/events/calendar-sources/${id}/sync`),
  // Public
  getUpcoming: (params) => api.get('/events/upcoming', { params }),
  getNearby: (lat, lng, params) => api.get(`/events/nearby/${lat}/${lng}`, { params }),
  getPublicEvent: (idOrSlug) => api.get(`/events/${idOrSlug}`),
};

export const rewardsAPI = {
  // CMS endpoints
  getRewards: (params) => api.get('/cms/rewards', { params }),
  getRewardStats: () => api.get('/cms/rewards/stats'),
  getRewardById: (id) => api.get(`/cms/rewards/${id}`),
  createReward: (data) => api.post('/cms/rewards', data),
  updateReward: (id, data) => api.put(`/cms/rewards/${id}`, data),
  deleteReward: (id) => api.delete(`/cms/rewards/${id}`),
  getConditionTypes: () => api.get('/cms/rewards/condition-types'),
  uploadImage: (id, file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post(`/cms/rewards/${id}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

export const settingsAPI = {
  getSettings: () => api.get('/cms/settings'),
  saveSettings: (category, settings) => api.put(`/cms/settings/${category}`, settings),
  resetSettings: (category) => api.post(`/cms/settings/reset/${category}`),
  getArweaveBalance: () => api.get('/cms/settings/arweave/balance'),
  uploadArweaveWallet: (walletData) => api.post('/cms/settings/arweave/upload-wallet', { wallet: walletData })
};

// Secure Settings API (Encrypted API Keys)
export const secureSettingsAPI = {
  // Anthropic API Key
  getAnthropicKey: () => api.get('/cms/secure-settings/anthropic-key'),
  updateAnthropicKey: (apiKey) => api.put('/cms/secure-settings/anthropic-key', { apiKey }),

  // Replicate API Key (for AI image cartoonification)
  getReplicateKey: () => api.get('/cms/secure-settings/replicate-key'),
  updateReplicateKey: (apiKey) => api.put('/cms/secure-settings/replicate-key', { apiKey }),

  // Google Maps API Key (for geocoding)
  getGoogleMapsKey: () => api.get('/cms/secure-settings/googlemaps-key'),
  updateGoogleMapsKey: (apiKey) => api.put('/cms/secure-settings/googlemaps-key', { apiKey }),

  // WalletConnect Project ID (for mobile wallet connections)
  getWalletConnectKey: () => api.get('/cms/secure-settings/walletconnect-key'),
  updateWalletConnectKey: (apiKey) => api.put('/cms/secure-settings/walletconnect-key', { apiKey }),

  // Unsplash API Key (for stock photos)
  getUnsplashKey: () => api.get('/cms/secure-settings/unsplash-key'),
  updateUnsplashKey: (apiKey) => api.put('/cms/secure-settings/unsplash-key', { apiKey }),

  // MapBox API Key (for geocoding)
  getMapboxKey: () => api.get('/cms/secure-settings/mapbox-key'),
  updateMapboxKey: (apiKey) => api.put('/cms/secure-settings/mapbox-key', { apiKey }),

  // Wallet Status
  getWalletStatus: () => api.get('/cms/secure-settings/wallet-status'),
  updateAlgorandWallet: (mnemonic) => api.put('/cms/secure-settings/algorand-wallet', { mnemonic }),

  // Audit Log
  getAuditLog: (limit = 50, category = null) => {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (category) params.append('category', category);
    return api.get(`/cms/secure-settings/audit-log?${params.toString()}`);
  }
};

// Utility functions
export const handleAPIError = (error) => {
  if (error.response) {
    // Server responded with error status
    return {
      message: error.response.data?.message || 'Server error',
      status: error.response.status,
      data: error.response.data
    };
  } else if (error.request) {
    // Request made but no response received
    return {
      message: 'Network error. Please check your connection.',
      status: 0,
      data: null
    };
  } else {
    // Something else happened
    return {
      message: error.message || 'An unexpected error occurred',
      status: -1,
      data: null
    };
  }
};

export const isAuthenticated = () => {
  const token = localStorage.getItem('accessToken');
  if (!token) return false;

  try {
    // Basic token expiration check (without decoding JWT for security)
    const tokenData = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    
    return tokenData.exp > currentTime;
  } catch (error) {
    return false;
  }
};

export const clearAuthTokens = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
};

export const setAuthTokens = (accessToken, refreshToken) => {
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
};

export default api;